import { useMemo } from 'react';
import useForm from './useForm';
import api from '../api/client';
import { required, email, phone, name, url, minLength, maxLength } from '../utils/validators';

const CONTACT_SCHEMA = {
  name: [required('Enter your name'), name(), maxLength(150)],
  email: [required('Enter your email address'), email()],
  phone: [required('Enter your phone number'), phone()],
  website: [url()],
  message: [
    required('Tell us what you need'),
    minLength(10, 'Tell us a little more — at least 10 characters'),
    maxLength(5000),
  ],
};

const EMPTY = {
  name: '', phone: '', email: '', website: '', message: '',
  company_website: '', // honeypot: hidden from people, filled by bots
};

/**
 * The enquiry form used on Home, Contact, Infra, IT, Marketing and Financial.
 *
 * `sourcePage` is what makes the admin console able to say which page produced
 * a lead, so pass the same name the page gives its tracker.
 *
 *   const { values, errors, isSubmitting, handleSubmit, fieldProps } =
 *     useContactForm('it-consulting');
 */
export default function useContactForm(sourcePage = 'unknown', options = {}) {
  const form = useForm({
    initialValues: EMPTY,
    schema: CONTACT_SCHEMA,
    formName: 'contact',
    page: sourcePage,
    scrollToTopOnSuccess: true,
    successMessage: 'Thanks — your message is with our team. We reply within one working day.',
    onSubmit: async (values) => {
      const { data } = await api.post('/api/contacts', {
        name: values.name,
        email: values.email,
        phone: values.phone,
        website: values.website || null,
        message: values.message,
        company_website: values.company_website,
        source_page: sourcePage,
      });
      return data;
    },
    ...options,
  });

  // Kept so existing JSX that reads `formData` and calls `handleInputChange`
  // keeps working without being rewritten.
  return useMemo(() => ({
    ...form,
    formData: form.values,
    handleInputChange: form.handleChange,
    isLoading: form.isSubmitting,
  }), [form]);
}
